import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, ReactElement } from 'react';
import { FormSpy } from 'react-final-form';

import {
  Form,
  IFormContentProps,
  Field,
  ImageType,
  ImageUploader,
  MultiLineField,
  DropDown,
  RadioGroup,
  TextField,
} from '@gem/components';
import { useTranslation } from '@gem/modules/i18n';
import { Button, ButtonTypes, customScrollStyles, PopupFooter } from '@gem/ui-kit';

import { Avatar, AvatarTypes, TAvatarBackgroundColorIndex } from 'APP/components/Avatar/Avatar';

import { SpaceDeepLinkAliasField } from './SpaceDeepLinkAliasField/SpaceDeepLinkAliasField';
import { CreateSpaceStep, ISpaceFormData, useSpaceFormPresenter } from './SpaceForm.presenter';
import styles from './SpaceForm.styles.m.css';

const FORM_ID = 'space-form';
const SPACE_NAME_MAX_LENGTH = 35;
const SPACE_DESCRIPTION_MAX_LENGTH = 400;

interface ISpaceFormProps {
  initialValues: ISpaceFormData;
  avatarTitle?: string;
  avatarColor?: TAvatarBackgroundColorIndex | null;
  isEditMode?: boolean;
  step?: CreateSpaceStep;
  onStepChange?(step: CreateSpaceStep): void;
  onFormStateChange(state: Partial<IFormContentProps>): void;
  onSubmit(data: ISpaceFormData): Promise<void>;
}

export const SpaceForm: FC<ISpaceFormProps> = observer(
  ({
    initialValues,
    avatarTitle,
    avatarColor,
    isEditMode = false,
    step: newStep,
    onStepChange,
    onFormStateChange,
    onSubmit,
  }) => {
    const { t } = useTranslation();
    const presenter = useSpaceFormPresenter({ initialValues, isEditMode, newStep, onStepChange });

    return (
      <Form
        initialValues={presenter.initialValues}
        subscription={{
          submitting: true,
          pristine: true,
          invalid: true,
          validating: true,
        }}
        onSubmit={presenter.isShowSubmit ? onSubmit : presenter.onShowAdditionalInfo}
      >
        {({
          handleSubmit,
          submitting,
          pristine,
          invalid,
          validating,
        }: IFormContentProps): ReactElement => (
          <>
            <FormSpy
              subscription={{ submitting: true, pristine: true }}
              onChange={onFormStateChange}
            />

            <form
              id={FORM_ID}
              className={classNames(styles.form, customScrollStyles.container)}
              onSubmit={handleSubmit}
            >
              <div
                className={classNames(
                  presenter.isShowGeneralInfo ? null : styles.hide,
                  isEditMode ? styles.sectionIndent : null
                )}
              >
                <Field
                  name="avatarUrl"
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  component={ImageUploader}
                  imageType={ImageType.SQUARE}
                  onLoad={presenter.onImageLoad}
                  renderImage={(url: string): ReactElement => (
                    <Avatar
                      url={url}
                      type={AvatarTypes.Square_50}
                    />
                  )}
                  customNoImage={
                    avatarTitle &&
                    avatarColor && (
                      <Avatar
                        type={AvatarTypes.Square_50}
                        title={avatarTitle}
                        backgroundColorIndex={avatarColor}
                      />
                    )
                  }
                />

                <Field
                  className={styles.textField}
                  name="name"
                  label={t('space_name_hint')}
                  isNeedTrim
                  component={TextField}
                  maxLength={SPACE_NAME_MAX_LENGTH}
                  validate={presenter.nameValidate}
                />

                <Field
                  className={styles.textField}
                  name="categoryId"
                  label={t('chat_details_category_label')}
                  component={DropDown}
                  options={presenter.categories}
                  validate={presenter.categoryValidate}
                />

                <Field
                  isNeedTrim
                  name="description"
                  label={t('space_description_hint')}
                  component={MultiLineField}
                  maxLength={SPACE_DESCRIPTION_MAX_LENGTH}
                />
              </div>

              <div className={presenter.isShowAdditionalInfo ? null : styles.hide}>
                <RadioGroup
                  className={styles.radioGap}
                  name="type"
                  title={t('space_type')}
                  options={presenter.typeOptions}
                  validateFields={['deepLinkAlias']}
                />

                <Field
                  isNeedTrim
                  name="deepLinkAlias"
                  component={SpaceDeepLinkAliasField}
                  validate={presenter.aliasValidate}
                  key={presenter.isShowAdditionalInfo ? 1 : 0}
                />
              </div>
            </form>

            <PopupFooter>
              <Button
                type={ButtonTypes.Submit}
                form={FORM_ID}
                isLoading={submitting}
                isDisabled={pristine || invalid || validating}
                theme="primary"
              >
                {presenter.isShowSubmit ? t('common_save') : t('action_next')}
              </Button>
            </PopupFooter>
          </>
        )}
      </Form>
    );
  }
);
